import { useState, useEffect, useMemo } from "react";
import { getCubiclGroups, getCubiclTasks, updateCubiclTaskState, getCubiclUsers, getCubiclTaskActivities } from "../services/api";

const PROJE_ADI = "EMK Yazılım";

const DURUM_ETIKET = {
  cb_waiting: "Bekliyor",
  cb_active: "Aktif",
  cb_completed: "Tamamlandı",
  cb_suspended: "Askıya Alındı",
};

const DURUM_RENK = {
  cb_waiting: "#f59e0b",
  cb_active: "#3b82f6",
  cb_completed: "#22c55e",
  cb_suspended: "#ef4444",
};

const DURUM_SIRA = ["cb_waiting", "cb_active", "cb_suspended", "cb_completed"];

const FALLBACK_RENKLER = ["#a855f7", "#06b6d4", "#f97316", "#ec4899", "#10b981", "#8b5cf6"];

const ALAN_ETIKET = {
  name: "Başlık", desc: "Açıklama", deadline: "Son Tarih", start: "Başlangıç",
  priority: "Öncelik", assignees: "Atananlar", customer: "Müşteri", state: "Durum",
  tags: "Etiketler", group: "Proje", private: "Gizlilik", estimatedTime: "Tahmini Süre",
  steps: "Kontrol Listesi", followers: "Takipçiler",
};

function parseActivityContent(html) {
  if (!html) return { text: "", images: [] };
  let doc;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return { text: "", images: [] };
  }
  doc.querySelectorAll("style, script, head").forEach((el) => el.remove());
  const images = Array.from(doc.querySelectorAll("img"))
    .map((img) => img.getAttribute("src") || "")
    .filter((src) => /^(https?:|data:image\/)/i.test(src));
  const text = (doc.body?.textContent || "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n\s*\n+/g, "\n\n")
    .trim();
  return { text, images };
}

function fmtTarih(unixSaniye) {
  if (!unixSaniye) return null;
  const d = new Date(unixSaniye * 1000);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function fmtTarihSaat(unixSaniye) {
  if (!unixSaniye) return null;
  const d = new Date(unixSaniye * 1000);
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function TaskCard({ task, dragging, saving, onDragStart, onDragEnd, onOpen }) {
  const deadline = fmtTarih(task.deadline);
  const bugun = Math.floor(Date.now() / 1000);
  const gecikmis = task.deadline && task.deadline < bugun && task.state !== "cb_completed";

  return (
    <div
      draggable={!saving}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(task)}
      title="Ayrıntıları göster"
      style={{
        background: "var(--bg-card)", border: "1px solid var(--border-hairline)", borderRadius: 8,
        padding: "10px 12px", marginBottom: 8,
        opacity: dragging ? 0.4 : saving ? 0.6 : 1,
        cursor: saving ? "wait" : "grab",
        transition: "opacity 0.15s ease",
      }}
    >
      <div style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 500, marginBottom: 6, lineHeight: 1.4 }}>
        {task.name || "(konu yok)"}
      </div>
      {task.customer?.name && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          👤 {task.customer.name}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {deadline && (
          <span style={{ fontSize: 11, color: gecikmis ? "#fca5a5" : "var(--text-faint)", fontWeight: gecikmis ? 600 : 400 }}>
            📅 {deadline}
          </span>
        )}
        {task.priority >= 4 && (
          <span style={{ fontSize: 11, color: "#fca5a5" }}>🔺 Öncelikli</span>
        )}
        {task.assignees?.length > 0 && (
          <span style={{ fontSize: 11, color: "var(--text-faint)" }}>👥 {task.assignees.length}</span>
        )}
        {saving && <span style={{ fontSize: 11, color: "var(--text-faint)" }}>⏳</span>}
      </div>
    </div>
  );
}

function CheckListItem({ step }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: step.completed ? "var(--text-faint)" : "var(--text-primary)" }}>
        <span>{step.completed ? "☑" : "☐"}</span>
        <span style={{ textDecoration: step.completed ? "line-through" : "none" }}>{step.name}</span>
      </div>
      {step.steps?.length > 0 && (
        <div style={{ marginLeft: 22, marginTop: 4 }}>
          {step.steps.map((s) => (
            <CheckListItem key={s.id} step={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityItem({ activity, assigneeNames }) {
  const author =
    activity.emailDetails?.from?.name ||
    (activity.user && assigneeNames[activity.user]) ||
    "Sistem";
  const tarih = fmtTarihSaat(activity.createdAt);

  let icon = "💬";
  let govde = null;

  if (activity.type === "post") {
    icon = "💬";
    const { text, images } = parseActivityContent(activity.content || "");
    govde = (text || images.length > 0) ? (
      <div>
        {text && <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>}
        {images.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: text ? 8 : 0 }}>
            {images.map((src, i) => (
              <img key={i} src={src} alt="" style={{ maxWidth: 220, maxHeight: 220, borderRadius: 6, border: "1px solid var(--border-hairline)", objectFit: "cover" }} />
            ))}
          </div>
        )}
      </div>
    ) : null;
  } else if (activity.type === "state-change") {
    icon = "🔀";
    const eski = DURUM_ETIKET[activity.data?.oldState?.name] || activity.data?.oldState?.name;
    const yeni = DURUM_ETIKET[activity.data?.newState?.name] || activity.data?.newState?.name;
    govde = <div style={{ color: "var(--text-muted)" }}>Durum değişti: <strong style={{ color: "var(--text-primary)" }}>{eski}</strong> → <strong style={{ color: "var(--text-primary)" }}>{yeni}</strong></div>;
  } else if (activity.type === "update") {
    icon = "✏️";
    const alanlar = (activity.data?.updates || []).map((k) => ALAN_ETIKET[k] || k);
    govde = <div style={{ color: "var(--text-muted)" }}>Güncellendi: {alanlar.join(", ") || "—"}</div>;
  } else if (activity.type === "progress") {
    icon = "☑️";
    govde = <div style={{ color: "var(--text-muted)" }}>Kontrol listesinde ilerleme kaydedildi.</div>;
  } else {
    govde = <div style={{ color: "var(--text-muted)" }}>{activity.type}</div>;
  }

  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      <div style={{ fontSize: 14, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>{author}</span>
          {tarih && <span style={{ fontSize: 11, color: "var(--text-faint)" }}>{tarih}</span>}
        </div>
        <div style={{ fontSize: 13 }}>{govde}</div>
      </div>
    </div>
  );
}

function TaskDetailModal({ task, groupId, assigneeNames, loadingAssignees, activities, loadingActivities, onClose }) {
  const durumEtiket = DURUM_ETIKET[task.state] || task.state.replace(/\s+/g, " ").trim();
  const durumRenk = DURUM_RENK[task.state] || "#a855f7";
  const isChecklist = task.stepType === "steps" && Array.isArray(task.steps);
  const isProgress = task.stepType === "progress" && task.steps;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--bg-page)", border: "1px solid var(--border)", borderRadius: 12, width: 560, maxHeight: "84vh", display: "flex", flexDirection: "column", boxShadow: "0 25px 50px rgba(0,0,0,0.5)" }}
      >
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--border-hairline)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <span style={{ display: "inline-block", fontSize: 11, fontWeight: 700, color: durumRenk, border: `1px solid ${durumRenk}`, borderRadius: 99, padding: "2px 10px", marginBottom: 8 }}>
              {durumEtiket}
            </span>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-heading)", lineHeight: 1.4 }}>{task.name || "(konu yok)"}</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--text-muted)", fontSize: 20, cursor: "pointer", lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>

        <div style={{ padding: "18px 22px", overflowY: "auto", flex: 1 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 24px", marginBottom: 18, fontSize: 13 }}>
            {task.customer?.name && (
              <div><span style={{ color: "var(--text-faint)" }}>👤 Müşteri: </span><span style={{ color: "var(--text-primary)" }}>{task.customer.name}</span></div>
            )}
            {task.deadline && (
              <div><span style={{ color: "var(--text-faint)" }}>📅 Son Tarih: </span><span style={{ color: "var(--text-primary)" }}>{fmtTarihSaat(task.deadline)}</span></div>
            )}
            {task.priority != null && (
              <div><span style={{ color: "var(--text-faint)" }}>Öncelik: </span><span style={{ color: "var(--text-primary)" }}>{task.priority}/5</span></div>
            )}
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Açıklama</div>
            <div style={{ fontSize: 13, color: task.desc ? "var(--text-primary)" : "var(--text-ghost)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
              {task.desc || "Açıklama girilmemiş."}
            </div>
          </div>

          {isChecklist && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>Kontrol Listesi</div>
              {task.steps.length === 0
                ? <div style={{ fontSize: 13, color: "var(--text-ghost)" }}>Adım yok.</div>
                : task.steps.map((s) => <CheckListItem key={s.id} step={s} />)}
            </div>
          )}

          {isProgress && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>İlerleme</div>
              <div style={{ background: "var(--bg-card)", borderRadius: 99, height: 8, overflow: "hidden" }}>
                <div style={{ width: `${task.steps.total ? (task.steps.completed / task.steps.total) * 100 : 0}%`, height: "100%", background: "var(--accent)" }} />
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>{task.steps.completed} / {task.steps.total}</div>
            </div>
          )}

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>Atananlar</div>
            {loadingAssignees ? (
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>⏳ Yükleniyor...</div>
            ) : task.assignees?.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {task.assignees.map((id) => (
                  <span key={id} style={{ fontSize: 12, color: "var(--text-primary)", background: "var(--bg-card)", borderRadius: 99, padding: "3px 10px" }}>
                    {assigneeNames[id] || "…"}
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "var(--text-ghost)" }}>Atanan kimse yok.</div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, marginBottom: 10, textTransform: "uppercase" }}>Aktiviteler</div>
            {loadingActivities ? (
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>⏳ Yükleniyor...</div>
            ) : activities?.length > 0 ? (
              activities
                .slice()
                .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
                .map((a) => <ActivityItem key={a._id} activity={a} assigneeNames={assigneeNames} />)
            ) : (
              <div style={{ fontSize: 13, color: "var(--text-ghost)" }}>Henüz aktivite yok.</div>
            )}
          </div>
        </div>

        <div style={{ padding: "14px 22px", borderTop: "1px solid var(--border-hairline)", display: "flex", justifyContent: "flex-end" }}>
          <a
            href={`https://cubicl.io/group/${groupId}/tasks/${task._id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: "var(--accent-text)", textDecoration: "none", fontWeight: 600 }}
          >
            Cubicl'de Aç ↗
          </a>
        </div>
      </div>
    </div>
  );
}

export default function DestekKayitlari() {
  const [group, setGroup] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [draggingId, setDraggingId] = useState(null);
  const [dragOverState, setDragOverState] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  const [detailTask, setDetailTask] = useState(null);
  const [assigneeNames, setAssigneeNames] = useState({});
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [activities, setActivities] = useState(null);
  const [loadingActivities, setLoadingActivities] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getCubiclGroups()
      .then((r) => {
        const emk = (r.data || []).find((g) => g.name === PROJE_ADI);
        if (!emk) {
          setError(`"${PROJE_ADI}" adlı Cubicl projesi bulunamadı.`);
          setLoading(false);
          return;
        }
        setGroup(emk);
        return getCubiclTasks(emk._id, { archived: "false", limit: 300 }).then((r2) => {
          setTasks(r2.data || []);
        });
      })
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!detailTask) {
      setActivities(null);
      return;
    }
    setLoadingActivities(true);
    getCubiclTaskActivities(detailTask._id)
      .then((r) => setActivities(r.data || []))
      .catch(() => setActivities([]))
      .finally(() => setLoadingActivities(false));
  }, [detailTask]);

  useEffect(() => {
    if (!detailTask) return;
    const ids = new Set(detailTask.assignees || []);
    (activities || []).forEach((a) => { if (a.user) ids.add(a.user); });
    const eksik = [...ids].filter((id) => !assigneeNames[id]);
    if (eksik.length === 0) return;
    setLoadingAssignees(true);
    getCubiclUsers(eksik)
      .then((r) => {
        const ek = {};
        (r.data || []).forEach((u) => {
          ek[u._id] = `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.email;
        });
        setAssigneeNames((prev) => ({ ...prev, ...ek }));
      })
      .catch(() => {})
      .finally(() => setLoadingAssignees(false));
  }, [detailTask, activities]);

  const kolonlar = useMemo(() => {
    if (!tasks) return [];
    const map = {};
    tasks.forEach((t) => {
      const key = t.state || "cb_active";
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    const bilinen = DURUM_SIRA.filter((s) => map[s]);
    const bilinmeyen = Object.keys(map).filter((s) => !DURUM_SIRA.includes(s));
    return [...bilinen, ...bilinmeyen].map((state, i) => {
      const items = state === "cb_waiting"
        ? [...map[state]].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        : map[state];
      return {
        state,
        label: DURUM_ETIKET[state] || state.replace(/\s+/g, " ").trim(),
        color: DURUM_RENK[state] || FALLBACK_RENKLER[i % FALLBACK_RENKLER.length],
        items,
      };
    });
  }, [tasks]);

  function handleDrop(targetState) {
    setDragOverState(null);
    const taskId = draggingId;
    setDraggingId(null);
    if (!taskId) return;

    const task = tasks.find((t) => t._id === taskId);
    if (!task || task.state === targetState) return;

    const eskiState = task.state;
    setTasks((prev) => prev.map((t) => (t._id === taskId ? { ...t, state: targetState } : t)));
    setSavingId(taskId);
    setActionMsg(null);

    updateCubiclTaskState(taskId, targetState)
      .catch((e) => {
        setTasks((prev) => prev.map((t) => (t._id === taskId ? { ...t, state: eskiState } : t)));
        setActionMsg({ type: "error", text: e.response?.data?.detail || e.message });
      })
      .finally(() => {
        setSavingId(null);
        setTimeout(() => setActionMsg(null), 5000);
      });
  }

  if (loading) return <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "40px 0", textAlign: "center" }}>⏳ Cubicl'den yükleniyor...</div>;
  if (error) return <div style={{ background: "#450a0a", border: "1px solid #7f1d1d", borderRadius: 8, padding: "12px 16px", color: "#fca5a5", fontSize: 13 }}>{error}</div>;

  return (
    <div>
      {detailTask && (
        <TaskDetailModal
          task={detailTask}
          groupId={group?._id}
          assigneeNames={assigneeNames}
          loadingAssignees={loadingAssignees}
          activities={activities}
          loadingActivities={loadingActivities}
          onClose={() => setDetailTask(null)}
        />
      )}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-heading)" }}>{group?.name}</div>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>{tasks?.length ?? 0} görev · Cubicl</div>
        </div>
        {actionMsg && (
          <div style={{ fontSize: 12, color: actionMsg.type === "error" ? "#fca5a5" : "#86efac", maxWidth: 320, textAlign: "right" }}>
            {actionMsg.type === "error" ? `Durum güncellenemedi: ${actionMsg.text}` : actionMsg.text}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 8 }}>
        {kolonlar.map((k) => (
          <div
            key={k.state}
            onDragOver={(e) => { e.preventDefault(); if (dragOverState !== k.state) setDragOverState(k.state); }}
            onDragLeave={() => setDragOverState((s) => (s === k.state ? null : s))}
            onDrop={(e) => { e.preventDefault(); handleDrop(k.state); }}
            style={{
              flexShrink: 0, width: 260,
              background: dragOverState === k.state ? "var(--bg-alt-row)" : "var(--bg-page)",
              border: dragOverState === k.state ? "1px dashed var(--accent)" : "1px solid var(--border-hairline)",
              borderRadius: 10, display: "flex", flexDirection: "column", maxHeight: "70vh",
              transition: "background 0.1s ease, border-color 0.1s ease",
            }}
          >
            <div style={{ padding: "10px 12px", borderTop: `3px solid ${k.color}`, borderBottom: "1px solid var(--border-hairline)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{k.label}</span>
              <span style={{ fontSize: 11, color: "var(--text-faint)", background: "var(--bg-card)", borderRadius: 99, padding: "1px 8px" }}>{k.items.length}</span>
            </div>
            <div style={{ padding: 10, overflowY: "auto", flex: 1 }}>
              {k.items.map((t) => (
                <TaskCard
                  key={t._id}
                  task={t}
                  dragging={draggingId === t._id}
                  saving={savingId === t._id}
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", t._id); setDraggingId(t._id); }}
                  onDragEnd={() => { setDraggingId(null); setDragOverState(null); }}
                  onOpen={setDetailTask}
                />
              ))}
              {k.items.length === 0 && (
                <div style={{ color: "var(--text-ghost)", fontSize: 12, textAlign: "center", padding: "16px 0" }}>—</div>
              )}
            </div>
          </div>
        ))}
        {kolonlar.length === 0 && (
          <div style={{ color: "var(--text-ghost)", fontSize: 14, padding: "40px 0" }}>Bu projede görev bulunamadı.</div>
        )}
      </div>
    </div>
  );
}
